import Cookies from "js-cookie";
import { B3_AUTH_COOKIE_NAME, B3OS_MASQUERADE_COOKIE_NAME, B3OS_ORG_COOKIE_NAME } from "@/lib/constants/cookies";
import { deleteCookie, setCookie } from "@/lib/cookies";

export const MASQUERADE_TOKEN_KEY = "b3os-masquerade-token";
export const MASQUERADE_ORG_ID_KEY = "b3os-masquerade-orgId";
export const MASQUERADE_INFO_KEY = "b3os-masquerade-info";
export const MASQUERADE_EMBEDDED_KEY = "b3os-masquerade-embedded";
export const MASQUERADE_CHANGED_EVENT = "b3os-masquerade-changed";

export interface MasqueradeSessionData {
  sessionId: string;
  targetEmail: string;
  targetOrgId: string;
  expiresAt: string;
}

export function getMasqueradeToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return sessionStorage.getItem(MASQUERADE_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getMasqueradeOrgId(): string | undefined {
  try {
    if (typeof window === "undefined") return undefined;
    return sessionStorage.getItem(MASQUERADE_ORG_ID_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function getMasqueradeInfo(): MasqueradeSessionData | null {
  try {
    const raw = sessionStorage.getItem(MASQUERADE_INFO_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as MasqueradeSessionData;
  } catch {
    return null;
  }
}

export function setMasqueradeSession(token: string, data: MasqueradeSessionData): void {
  sessionStorage.setItem(MASQUERADE_TOKEN_KEY, token);
  sessionStorage.setItem(MASQUERADE_ORG_ID_KEY, data.targetOrgId);
  sessionStorage.setItem(MASQUERADE_INFO_KEY, JSON.stringify(data));
  sessionStorage.setItem(MASQUERADE_EMBEDDED_KEY, "1");
  setCookie(B3OS_MASQUERADE_COOKIE_NAME, "1");
  window.dispatchEvent(new Event(MASQUERADE_CHANGED_EVENT));
}

export function clearMasqueradeSession(): void {
  sessionStorage.removeItem(MASQUERADE_TOKEN_KEY);
  sessionStorage.removeItem(MASQUERADE_ORG_ID_KEY);
  sessionStorage.removeItem(MASQUERADE_INFO_KEY);
  sessionStorage.removeItem(MASQUERADE_EMBEDDED_KEY);
  deleteCookie(B3OS_MASQUERADE_COOKIE_NAME);
  window.dispatchEvent(new Event(MASQUERADE_CHANGED_EVENT));
}

export function isEmbeddedMasquerade(): boolean {
  try {
    return sessionStorage.getItem(MASQUERADE_EMBEDDED_KEY) === "1";
  } catch {
    return false;
  }
}

export function isMasqueradeExpired(): boolean {
  const info = getMasqueradeInfo();
  if (!info?.expiresAt) return false;
  return new Date(info.expiresAt).getTime() <= Date.now();
}

export interface SSEAuthCredentials {
  authHeaders: Record<string, string>;
  orgId: string;
}

export function getSSEAuthCredentials(): SSEAuthCredentials | null {
  const masqueradeToken = getMasqueradeToken();
  const accessToken = masqueradeToken ? null : Cookies.get(B3_AUTH_COOKIE_NAME);
  if (!masqueradeToken && !accessToken) return null;

  const orgId = (masqueradeToken ? getMasqueradeOrgId() : null) ?? Cookies.get(B3OS_ORG_COOKIE_NAME);
  if (!orgId) return null;

  const authHeaders: Record<string, string> = masqueradeToken
    ? { "X-Masquerade-Token": masqueradeToken }
    : { Authorization: `Bearer ${accessToken!}` };

  return { authHeaders, orgId };
}
